import React from "react";
import { Zap } from "lucide-react";
import { Toast, ToastTitle, ToastDescription, ToastClose } from "./Toast";
import { ToastIconShell } from "./ToastIconShell";

/** How many rows the stack renders before collapsing into a count. */
export const AUTOMATION_TOAST_VISIBLE_LIMIT = 3;

/**
 * How long AutomationToastManager holds the shared single toast slot before
 * auto-dismissing. Lives here (rather than in AutomationToastManager) so
 * consumers — e.g. AutomationToastManager's own dismiss timer — don't need
 * to import that manager's internals.
 */
export const AUTOMATION_TOAST_AUTO_DISMISS_MS = 10_000;

export interface AutomationToastItem {
  key: string;
  cardId: string;
  headline: string;
  /** Resolved field values, already filtered of empties. */
  lines: string[];
}

export interface AutomationToastStackProps {
  items: AutomationToastItem[];
  overflow: number;
  onOpenCard?: (cardId: string) => void;
  onDismiss?: () => void;
}

export const AutomationToastStack = React.forwardRef<
  React.ElementRef<typeof Toast>,
  AutomationToastStackProps
>(({ items, overflow, onOpenCard, onDismiss, ...props }, ref) => (
  // Radix's own auto-dismiss timer is disabled here (as OverdueTaskToast
  // also does) so there is exactly one clock in play: the manager's
  // ref-held setTimeout. Without this, Radix's 5s default would race the
  // manager's ~10s auto-dismiss and the visible on-screen behavior would
  // not match the design.
  <Toast ref={ref} {...props} duration={Infinity}>
    <div className={"flex w-full flex-col divide-y divide-border"}>
      {items.map((item) => (
        <button
          key={item.key}
          type={"button"}
          className={"w-full py-2 text-left first:pt-0 last:pb-0"}
          onClick={() => onOpenCard?.(item.cardId)}
        >
          <ToastIconShell icon={<Zap className={"h-4 w-4"} />}>
            <ToastTitle>{item.headline}</ToastTitle>
            {item.lines.length > 0 && (
              <ToastDescription>{item.lines.join(" · ")}</ToastDescription>
            )}
          </ToastIconShell>
        </button>
      ))}
      {overflow > 0 && (
        <div className={"pt-2 text-center text-sm text-muted-foreground"}>
          + {overflow} more
        </div>
      )}
    </div>
    <ToastClose onClick={onDismiss} />
  </Toast>
));

AutomationToastStack.displayName = "AutomationToastStack";
